import {
  createSlice,
  createAsyncThunk,
  createListenerMiddleware,
  isAnyOf,
} from '@reduxjs/toolkit';
import { instance } from '@app/api/axios';
import { cardsParams, cardsFilters } from '@utils/tables/params';
import { calculateFiltersCount } from '@utils/filters-helpers';
import { showToaster } from '@shared/ui/messages/useToaster';
import i18n from '@app/i18n';

const countFilters = (params) => calculateFiltersCount(params, cardsFilters);

const applyParams = (state, action) => {
  state.params = { ...state.params, ...action.payload };
  state.filtersCount = countFilters(state.params);
};

export const fetchCards = createAsyncThunk(
  'cards/fetchCards',
  async (_, { rejectWithValue, getState }) => {
    const { params, search } = getState().cards;

    const merged = { ...cardsParams, ...params };
    const query = {
      page: merged.page,
      per_page: merged.per_page,
      sort_by: merged.sort_by,
      sort_dir: merged.sort_dir,
      ...(search && { search }),
      ...Object.keys(cardsFilters).reduce((acc, key) => {
        if (merged[key] || merged[key] === 0) {
          acc[key] = merged[key];
        }
        return acc;
      }, {}),
    };

    try {
      const res = await instance.get('/card/cards', { params: query });
      return res.data;
    } catch (err) {
      showToaster(
        'error',
        err?.response?.data?.message || i18n.t('request_error'),
      );
      return rejectWithValue(err.response?.data);
    }
  },
);

const cardsSlice = createSlice({
  name: 'cards',
  initialState: {
    cards: [],
    count_total: 0,
    count_filtered: 0,
    params: cardsParams,
    search: null,
    filtersCount: 0,
    selected: [],
    isLoading: true,
    isInitialized: false,
  },
  reducers: {
    setCardsParams: applyParams,
    setCardsParamsNoFetch: applyParams,
    setCardsSearch: (state, action) => {
      state.search = action.payload;
      state.params = { ...state.params, page: 1 };
      if (state.cards?.length === 0) {
        state.isInitialized = false;
      }
    },
    setCardsSearchNoFetch: (state, action) => {
      state.search = action.payload;
    },
    resetCardsFilters: (state) => {
      state.params = {
        ...state.params,
        ...cardsFilters,
        page: 1,
        filter: state.params.filter,
      };
      state.filtersCount = 0;
    },
    setSelected: (state, action) => {
      state.selected = action.payload;
    },
    resetAll: (state) => {
      state.params = cardsParams;
      state.search = null;
      state.selected = [];
      state.filtersCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCards.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchCards.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isInitialized = true;
      state.cards = action.payload?.cards;
      state.count_total = action.payload?.count_total;
      state.count_filtered = action.payload?.count_filtered;
      state.filtersCount = countFilters(state.params);
    });
    builder.addCase(fetchCards.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  setCardsParams,
  setCardsParamsNoFetch,
  setCardsSearch,
  setCardsSearchNoFetch,
  resetCardsFilters,
  setSelected,
  resetAll,
} = cardsSlice.actions;

export default cardsSlice.reducer;

export const cardsListener = createListenerMiddleware();

cardsListener.startListening({
  matcher: isAnyOf(setCardsParams, setCardsSearch, resetCardsFilters),
  effect: async (_, listenerApi) => {
    await listenerApi.dispatch(fetchCards());
  },
});
